import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { axiosPrivate } from '../../modules/networkTools/networkTools';

type MedicalVisitSource = 'Manual' | 'Updated' | 'Data Feed';

export type MedicalVisitsItem = {
  id?: number;
  dateOfEncounter?: string | null;
  timeOfEncounter?: string | null;
  location?: string | null;
  type?: string | null;
  typeId?: number;
  provider?: string | null;
  reason?: string | null;
  status?: string | null;
  statusId?: number;
  source?: MedicalVisitSource;
};

export type MedicalVisitsFilters = {
  typeId?: string | string[] | null;
  statusId?: string | string[] | null;
  startDate?: string | null;
  endDate?: string | null;
};

type ClientId = { clientId: string | number };

export const getMedicalVisitsPreview = createAsyncThunk(
  'medicalVisits/getMedicalVisitsPreview',
  async ({ clientId }: ClientId, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`client/${clientId}/medical-visits/preview`);
      return response;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMedicalVisits = createAsyncThunk(
  'medicalVisits/getMedicalVisits',
  async (
    {
      clientId,
      page,
      size,
      sorts,
      filters
    }: {
      clientId: string | number;
      page: number;
      size: number;
      sorts: { title: string; direction: string }[] | null;
      filters: MedicalVisitsFilters | null;
    },
    { rejectWithValue }
  ) => {
    try {
      let params: string | string[] = '';
      if (Array.isArray(sorts) && sorts.length > 0) {
        params = sorts
          .filter((item) => item.direction && item.title)
          .map((item) =>
            item.title === 'dateTime'
              ? `&sort=dateOfEncounter,${item.direction}&sort=timeOfEncounter,${item.direction}`
              : `&sort=${item.title},${item.direction}`
          )
          .join('');
      }

      if (filters) {
        const filterParams = Object.entries(filters)
          .filter(([_, value]) => value != null && value !== '')
          .map(([key, value]) => `&${key}=${encodeURIComponent(String(value))}`)
          .join('');
        params += filterParams;
      }

      const response = await axiosPrivate.get(
        `client/${clientId}/medical-visits?page=${page}&size=${size}${params}`
      );
      return { content: response.data, page, size, sorts, filters };
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const postMedicalVisits = createAsyncThunk(
  'medicalVisits/postMedicalVisits',
  async (
    { clientId, data }: { clientId: string | number; data: MedicalVisitsItem },
    { rejectWithValue }
  ) => {
    try {
      await axiosPrivate.post(`client/${clientId}/medical-visits`, data);
      return data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putMedicalVisits = createAsyncThunk(
  'medicalVisits/putMedicalVisits',
  async (
    {
      clientId,
      visitId,
      data
    }: {
      clientId: string | number;
      visitId: string | number;
      data: MedicalVisitsItem | any;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.put(`client/${clientId}/medical-visits/${visitId}`, data);
      return response;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const deleteMedicalVisits = createAsyncThunk(
  'medicalVisits/deleteMedicalVisits',
  async (
    {
      clientId,
      visitId
    }: {
      clientId: string | number;
      visitId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      await axiosPrivate.delete(`client/${clientId}/medical-visits/${visitId}`);

      return visitId;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMedicalVisitsTypes = createAsyncThunk(
  'medicalVisits/getMedicalVisitsTypes',
  async ({ clientId }: ClientId, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`client/${clientId}/medical-visits/types`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getMedicalVisitsStatuses = createAsyncThunk(
  'medicalVisits/getMedicalVisitsStatuses',
  async ({ clientId }: ClientId, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`client/${clientId}/medical-visits/statuses`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
